import { PButton } from "@porsche-design-system/components-react"
import type { VisibilityEditView, VisibilityValues, VisibilityView } from "@slfinrtl/ui-bff-routes"
import { updateVisibilityRoute, MarketToCustomers } from "@slfinrtl/ui-bff-routes"
import { useCallback, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { FormattedMessage } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { getDifference } from "src/common/getDifference/getDifference"
import { ButtonGroup, useVisibilityFields, VisibilityForm } from "src/components"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { useModal } from "src/context/modal/useModal"
import { useBffDynamic, useCurrencyLimit } from "src/hooks"
import type { PriceConfirmationModalProps } from "src/modals/priceConfirmationModal/PriceConfirmationModal"
import { PriceConfirmationModal } from "src/modals/priceConfirmationModal/PriceConfirmationModal"
import { priceConfirmationModalMessages } from "src/modals/priceConfirmationModal/priceConfirmationModal.messages"

interface VisibilityFormContainerProps {
	editView: VisibilityEditView
	inventoryId: string
	onCancel: () => void
	onSaved: (view: VisibilityView) => void
}

export const VisibilityFormContainer = withErrorHandler(
	({ editView, inventoryId, onCancel, onSaved }: VisibilityFormContainerProps) => {
		const [isSaving, setIsSaving] = useState(false)
		const showModal = useModal<PriceConfirmationModalProps>(PriceConfirmationModal)
		const { isPriceBelowCurrencyLimit } = useCurrencyLimit()

		const form = useForm<VisibilityValues>({
			defaultValues: editView.values,
			mode: "onChange",
		})

		const { handleSubmit } = form

		const fields = useVisibilityFields()

		const patch = useBffDynamic(updateVisibilityRoute, "inline")

		const onValid = (val: VisibilityValues) => {
			if (
				editView.price &&
				editView.modelYear &&
				isPriceBelowCurrencyLimit(editView.price, editView.modelYear) &&
				val.marketToCustomers === MarketToCustomers.VISIBLE
			) {
				showModal({
					currentPrice: editView.price,
					headline: priceConfirmationModalMessages.vehiclePriceLowHeadline,
					label: priceConfirmationModalMessages.vehiclePriceLowLabel,
					onConfirmClicked: () => {
						save(val)
					},
				})
			} else {
				save(val)
			}
		}

		const save = useCallback(
			(values: VisibilityValues) => {
				const patchedValues = values ? getDifference<VisibilityValues>(values, editView.values) : undefined

				setIsSaving(true)
				return patch({
					body: patchedValues ?? {},
					pathParams: { inventoryId },
					queryParams: {},
				})
					.then(onSaved)
					.finally(() => setIsSaving(false))
			},
			[inventoryId, onSaved, patch, editView.values],
		)

		return (
			<FormProvider {...form}>
				<ErrorNotificationInline />
				<VisibilityForm fields={fields} />

				<ButtonGroup>
					<PButton loading={isSaving} onClick={handleSubmit(onValid)}>
						<FormattedMessage {...formMessages.save} />
					</PButton>
					<PButton type="button" variant="secondary" onClick={onCancel}>
						<FormattedMessage {...formMessages.cancel} />
					</PButton>
				</ButtonGroup>
			</FormProvider>
		)
	},
)
